/**
 * SSO hand-off (backend: src/core/sso_handoff.py in useradmin).
 *
 * Tokens never travel in a URL. Before leaving for the identity provider we
 * keep a random verifier in sessionStorage and send only its SHA-256 (the
 * challenge). After sign-in the API redirects to /auth/callback#code=…, and
 * OAuthCallbackPage trades code + verifier for the tokens. Only this browser
 * tab holds the verifier, so a leaked or planted code is useless.
 */
import axios from 'axios';
import { API_PREFIX, getAbsoluteApiUrl } from '@/api/endpoints/api.constants';

const VERIFIER_KEY = 'sso_verifier';

const base64url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** Full-page redirect to the provider's login, carrying a fresh challenge. */
export async function startSso(protocol: 'oauth' | 'saml', providerId: number | string): Promise<void> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const path = protocol === 'saml' ? `/saml/login/${providerId}` : `/auth/oauth/login/${providerId}`;
  window.location.href = `${getAbsoluteApiUrl(path)}?challenge=${base64url(new Uint8Array(digest))}`;
}

/** Trade the one-time code for tokens. Throws if the code is invalid, expired
 *  or was not issued to this tab. */
export async function redeemSsoCode(code: string): Promise<{ access_token: string; refresh_token: string }> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!verifier) throw new Error('No sign-in in progress in this tab');
  // Plain axios: no auth header / refresh interceptors for this public call.
  const res = await axios.post(`${API_PREFIX}/auth/sso/exchange`, { code, verifier }, { withCredentials: true });
  return res.data;
}
